'use client';

import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import { usePathname } from 'next/navigation';

// Create the context with a default value (which will be overridden by the provider)
const NrfGlobalAudioPlayerContext = createContext<
  | {
      globallyPlayingStorageKey: string | null;
      isPlaying: boolean;
      play: (params: {
        storageKey: string;
        getUrl: () => Promise<string | null>;
      }) => Promise<void>;
      stop: () => void;
    }
  | undefined
>(undefined);

// Context provider component
export function NrfGlobalAudioPlayerProvider({
  children,
}: {
  children: ReactNode;
}) {
  const pathname = usePathname();

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const requestIdRef = useRef(0);

  const [globallyPlayingStorageKey, setGloballyPlayingStorageKey] = useState<
    string | null
  >(null);
  const [isPlaying, setIsPlaying] = useState(false);

  const stop = useCallback(() => {
    requestIdRef.current += 1; // invalidate any in-flight play() calls

    const audio = audioRef.current;
    if (audio) {
      audio.pause();
      audio.currentTime = 0;
      audio.onended = null;
    }

    audioRef.current = null;
    setIsPlaying(false);
    setGloballyPlayingStorageKey(null);
  }, []);

  const play = useCallback(
    async (params: {
      storageKey: string;
      getUrl: () => Promise<string | null>;
    }) => {
      const { storageKey, getUrl } = params;

      requestIdRef.current += 1;
      const requestId = requestIdRef.current;

      // Set key immediately so UI can reflect selection (even while URL is loading)
      setGloballyPlayingStorageKey(storageKey);
      setIsPlaying(false);

      let url: string | null = null;
      try {
        url = await getUrl();
      } catch (error) {
        console.error('Failed to resolve audio URL', error);
      }

      if (requestIdRef.current !== requestId) {
        // A newer play/stop happened while awaiting URL.
        return;
      }

      if (!url) {
        stop();
        return;
      }

      // Stop any currently playing audio before starting a new one.
      const existing = audioRef.current;
      if (existing) {
        existing.pause();
        existing.currentTime = 0;
        existing.onended = null;
      }

      const audio = new Audio(url);
      audioRef.current = audio;

      audio.onended = () => {
        // Only stop if this is still the current audio request.
        if (audioRef.current === audio) {
          stop();
        }
      };

      try {
        await audio.play();
        if (requestIdRef.current !== requestId) {
          audio.pause();
          return;
        }
        setIsPlaying(true);
      } catch (error) {
        console.error('Failed to start audio playback', error);
        if (audioRef.current === audio) {
          stop();
        }
      }
    },
    [stop],
  );

  // Stop audio on route change (and on provider unmount).
  useEffect(() => {
    stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  return (
    <NrfGlobalAudioPlayerContext.Provider
      value={{
        globallyPlayingStorageKey,
        isPlaying,
        play,
        stop,
      }}
    >
      {children}
    </NrfGlobalAudioPlayerContext.Provider>
  );
}

// Custom hook to use the audio player context
export function useNrfGlobalAudioPlayer() {
  const context = useContext(NrfGlobalAudioPlayerContext);
  if (!context) {
    throw new Error(
      'useNrfGlobalAudioPlayer must be used within an NrfGlobalAudioPlayerProvider',
    );
  }
  return context;
}
